import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { selectMatterById } from '@/matter/store/selectors'
import { useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import { selectMedChronAssetForMatter } from '@/medical-chronology/asset/store/selectors'
import MedChronAssetChatButton from '@/medical-chronology/asset/components/MedChronAssetChatButton'
import MedChronAssetDownloadButton from '@/medical-chronology/asset/components/MedChronAssetDownloadButton'
import { matterPaths } from '@/routing/paths'
import { Header } from '@/components/header/Header'
import type { BreadcrumbItemType } from '@/components/breadcrumbs/BreadcrumbItem'

/**
 * Breadcrumbs for the living Medical Chronology: Matter > Medical Chronology, with
 * Ask Assistant + Download actions once the chronology has events.
 */
export default function MatterBreadcrumbsMedChronAsset({ showAddFilesCrumb = false }: { showAddFilesCrumb?: boolean } = {}) {
  const navigate = useNavigate()

  const activeMatterId = useAppSelector((state: RootState) => state.mattersState.activeMatterId)
  const initialLoadComplete = useAppSelector((state: RootState) => state.mattersState.initialLoadComplete)
  const matter = useAppSelector((state: RootState) => selectMatterById(state, { matterId: activeMatterId }))
  const asset = useAppSelector((state: RootState) => selectMedChronAssetForMatter(state, { matterId: activeMatterId ?? undefined }))

  const breadcrumbItems: BreadcrumbItemType[] = useMemo(() => {
    if (!initialLoadComplete) return []

    const items: BreadcrumbItemType[] = []
    if (matter) {
      items.push({
        id: `matter-${matter.id}`,
        label: matter.name,
        onClick: () => navigate(matterPaths.overview(matter.id)),
      })
    }
    items.push({
      id: 'med-chron',
      label: 'Medical Chronology',
      // On the Add Files sub-page, Medical Chronology becomes a clickable parent crumb.
      ...(showAddFilesCrumb && activeMatterId ? { onClick: () => navigate(matterPaths.chronology.index(activeMatterId)) } : {}),
    })
    if (showAddFilesCrumb) {
      items.push({ id: 'add-files', label: 'Add Files' })
    }
    return items
  }, [initialLoadComplete, matter, navigate, showAddFilesCrumb, activeMatterId])

  const showActions = asset && asset.total_events > 0 && activeMatterId

  return (
    <Header
      breadcrumbs={breadcrumbItems}
      endContent={
        showActions ? (
          <div className="hidden lg:flex items-center gap-3.5">
            <MedChronAssetChatButton assetId={asset.id} matterId={activeMatterId} />
            <MedChronAssetDownloadButton assetId={asset.id} />
          </div>
        ) : undefined
      }
    />
  )
}
